import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Wallet, Search, Check, ChevronDown } from 'lucide-react';
import InputLabel from '../../../components/base/InputLabel';
import NumberInput from '../../../components/base/NumberInput';
import BigNumber from 'bignumber.js';
import { CONTRACTS } from '../config/contracts.ts';
import { formatNumber, formatMoney, isValidNumber } from '../../../utils/numberFormatting';

// Futures Parameter chip - similar to Crypto Parameter but adapted for futures fields
const FuturesParam = ({
  label,
  value,
  icon: Icon,
  onChange,
  onOpenSettings,
  isDirectEditable = false,
  prefix = '',
  suffix = '',
  staticDisplay
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const [width, setWidth] = useState(null);
  const paramRef = useRef(null);

  const getNumericValue = (val) => {
    if (val instanceof BigNumber) {
      return val.toString();
    }
    if (typeof val === 'string') {
      if (val.startsWith('$')) return val.slice(1);
      if (val.endsWith('%') || val.endsWith('x')) return val.slice(0, -1);
    }
    return val?.toString() || '0';
  };

  const formatValue = (val) => {
    if (val instanceof BigNumber) {
      const num = val.toNumber();
      if (prefix === '$') {
        // Compact formatting for money
        if (Math.abs(num) >= 1000) {
          return formatMoney(val, true);
        }
        return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
      }
      return `${prefix}${formatNumber(val, 2)}${suffix}`;
    }
    return `${prefix}${val}${suffix}`;
  };

  const handleClick = () => {
    if (!isEditing) {
      if (staticDisplay) {
        if (onOpenSettings) onOpenSettings(null);
        return;
      }
      if (isDirectEditable && paramRef.current) {
        setWidth(paramRef.current.offsetWidth);
        setIsEditing(true);
        setLocalValue(getNumericValue(value));
      } else if (onOpenSettings) {
        const field = label === 'Fee' ? 'roundTurnFee' : Icon === Wallet ? 'accountBalance' : null;
        onOpenSettings(field);
      }
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (!/^\d*\.?\d*$/.test(newValue)) return;
    setLocalValue(newValue);
    if (newValue && isValidNumber(newValue)) {
      onChange(new BigNumber(newValue));
    }
  };

  const handleBlur = () => {
    if (localValue && isValidNumber(localValue)) {
      onChange(new BigNumber(localValue));
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setLocalValue(getNumericValue(value));
    }
  };

  const baseClasses = "h-10 px-4 bg-slate-800 rounded-lg";

  if (isEditing) {
    return (
      <div
        className={`${baseClasses} border border-amber-500/20 focus-within:border-amber-500/50`}
        style={{ width: width ? `${width}px` : 'auto' }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-full h-full bg-transparent text-slate-200 text-sm outline-none"
        />
      </div>
    );
  }

  return (
    <div
      ref={paramRef}
      className={`${baseClasses} flex items-center hover:bg-slate-800/60 cursor-pointer border border-transparent`}
      onClick={handleClick}
    >
      {Icon ? (
        <Icon size={16} className="text-slate-500 mr-2 flex-shrink-0" />
      ) : label ? (
        <span className="text-slate-500 text-sm mr-2">{label}</span>
      ) : null}
      <span className="text-slate-300 text-sm ml-auto">{staticDisplay || formatValue(value)}</span>
    </div>
  );
};

// Contract Selector chip - custom dropdown with search (uses Portal)
const ContractSelectorChip = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update menu position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4 + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      setSearchQuery('');
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (symbol) => {
    onChange(symbol);
    setIsOpen(false);
  };

  // Filter contracts by search
  const filteredContracts = Object.entries(CONTRACTS).filter(([symbol, spec]) => {
    const query = searchQuery.toLowerCase();
    return symbol.toLowerCase().includes(query) || spec.name.toLowerCase().includes(query);
  });

  // Dropdown menu content (rendered via portal)
  const dropdownMenu = isOpen && createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] w-[400px]"
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        animation: 'dropdownIn 150ms ease-out'
      }}
    >
      <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        {/* Search Input */}
        <div className="p-3 border-b border-white/5">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search contracts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-10 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
            />
          </div>
        </div>

        {/* Options List */}
        <div className="py-2 max-h-[320px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
          {filteredContracts.map(([symbol, spec]) => (
            <div
              key={symbol}
              onClick={() => handleSelect(symbol)}
              className={`px-3 py-2.5 mx-2 rounded-lg cursor-pointer hover:bg-white/10 transition-colors flex items-center justify-between ${value === symbol ? 'bg-white/5' : ''}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-amber-400 font-mono font-bold w-12">{symbol}</span>
                <div>
                  <div className="text-slate-300 text-sm">{spec.name}</div>
                  <div className="text-slate-500 text-xs font-mono">
                    ${spec.pointValue}/pt · {spec.tickSize} tick · ${spec.tickValue}/tick
                  </div>
                </div>
              </div>
              {value === symbol && (
                <Check size={20} className="text-emerald-400 flex-shrink-0" />
              )}
            </div>
          ))}
          {filteredContracts.length === 0 && (
            <div className="px-4 py-3 text-slate-500 text-sm text-center">
              No contracts found
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="h-10 px-4 w-full lg:w-auto bg-slate-800 rounded-lg flex items-center justify-center lg:justify-start gap-2 hover:bg-slate-700 transition-colors border border-transparent hover:border-slate-600"
      >
        <span className="text-slate-300 text-sm">{value}</span>
        <ChevronDown
          size={14}
          className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu (via Portal) */}
      {dropdownMenu}
    </>
  );
};

const FuturesHeader = ({
  values,
  setValues,
  contractSpec,
  onOpenSettings,
  isCalculating
}) => {
  const handleContractChange = (symbol) => {
    setValues({ contract: symbol });
  };

  // Calculate decimals from tick size (e.g., 0.25 → 2, 0.01 → 2, 0.10 → 1)
  const tickDecimals = (contractSpec.tickSize.toString().split('.')[1] || '').length;

  // Round price to nearest tick (exchange requirement - non-tick prices are rejected)
  const roundToTick = (price) => {
    if (!price || !(price instanceof BigNumber)) return price;
    const tickSize = new BigNumber(contractSpec.tickSize);
    // Round to nearest tick: Math.round(price / tickSize) * tickSize
    return price.dividedBy(tickSize).integerValue(BigNumber.ROUND_HALF_UP).times(tickSize);
  };

  // Handle entry price change with auto-round to tick
  const handleEntryPriceChange = (value) => {
    const rounded = roundToTick(value);
    setValues({ entryPrice: rounded });
  };

  // Handle stop loss change with auto-round to tick
  const handleStopLossChange = (value) => {
    const rounded = roundToTick(value);
    setValues({ stopLoss: rounded });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col lg:flex-row gap-4 lg:gap-6 lg:items-start">
      {/* Price inputs - 2-col grid on mobile, inline on lg */}
      <div className="grid grid-cols-2 gap-3 lg:contents">
        {/* Entry Price - w-72 on lg (same as Crypto) */}
        <div className="w-full lg:w-72">
          <div className="flex justify-between items-center mb-2">
            <InputLabel variant="header" required>Entry price</InputLabel>
          </div>
          <NumberInput
            value={values.entryPrice}
            onChange={handleEntryPriceChange}
            decimals={tickDecimals}
            step={contractSpec.tickSize}
            required
            min={contractSpec.tickSize}
            name="entryPrice"
            copyEnabled={true}
          />
        </div>

        {/* Stop Loss - w-72 on lg (same as Crypto) */}
        <div className="w-full lg:w-72">
          <div className="flex justify-between items-center mb-2">
            <InputLabel variant="header" required>Stop-loss</InputLabel>
          </div>
          <NumberInput
            value={values.stopLoss}
            onChange={handleStopLossChange}
            decimals={tickDecimals}
            step={contractSpec.tickSize}
            required
            min={contractSpec.tickSize}
            name="stopLoss"
            copyEnabled={true}
          />
        </div>
      </div>

      {/* Parameters Section - flex-1 (same layout as Crypto) */}
      <div className="flex-1">
        <div className="flex justify-between items-center mb-2">
          {/* Contract specs - using InputLabel for consistent styling */}
          <InputLabel variant="header" className="!text-[13px] min-w-0 truncate">
            {contractSpec.name} | Tick: {contractSpec.tickSize} | ${contractSpec.tickValue}/tick | ${contractSpec.pointValue}/pt
          </InputLabel>
          {onOpenSettings && (
            <button
              onClick={() => onOpenSettings()}
              className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-400 transition-colors tracking-wide shrink-0"
            >
              Settings <Settings size={14} />
            </button>
          )}
        </div>

        {/* Parameter chips - responsive grid on mobile, single inline row on lg (contract selector spans full width on mobile) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex gap-2 lg:gap-3 mt-[6px]">
          {/* Contract Selector (replaces Leverage chip) - spans full width on mobile so its button fits */}
          <div className="col-span-2 sm:col-span-3 lg:col-auto">
            <ContractSelectorChip
              value={values.contract}
              onChange={handleContractChange}
            />
          </div>

          {/* Risk - with $/%  display based on unit */}
          <FuturesParam
            label="Risk"
            value={values.risk}
            prefix={values.riskUnit === 'fixed' ? '$' : ''}
            suffix={values.riskUnit === 'percent' ? '%' : ''}
            onChange={(value) => setValues({ risk: value })}
            isDirectEditable={true}
          />

          {/* R:R - desired net R:R */}
          <FuturesParam
            label={values.multiTpEnabled ? "TPs" : "R:R"}
            value={values.desiredRR}
            suffix="x"
            onChange={(value) => setValues({ desiredRR: value })}
            isDirectEditable={true}
            staticDisplay={values.multiTpEnabled ? String(values.tpCount) : undefined}
            onOpenSettings={onOpenSettings}
          />

          {/* Round Turn Fee (opens Settings) */}
          <FuturesParam
            label="Fee"
            value={values.roundTurnFee}
            prefix="$"
            onOpenSettings={onOpenSettings}
          />

          {/* Account Balance (opens Settings) */}
          <FuturesParam
            icon={Wallet}
            value={values.accountBalance}
            prefix="$"
            onOpenSettings={onOpenSettings}
          />
        </div>
      </div>
    </div>
  );
};

export default FuturesHeader;
